import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, InputNumber, Select, Typography, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { IPlanMeta } from '../../../types/plans';

const { Text } = Typography;

interface MetaEditorProps {
  value?: IPlanMeta;
  onChange?: (value: IPlanMeta) => void;
}

const MetaEditor: React.FC<MetaEditorProps> = ({ value = {}, onChange }) => {
  const [meta, setMeta] = useState<IPlanMeta>(value);

  useEffect(() => {
    setMeta(value);
  }, [value]);

  const updateMeta = (newValues: Partial<IPlanMeta>) => {
    const updatedMeta = { ...meta, ...newValues };
    setMeta(updatedMeta);
    onChange?.(updatedMeta);
  };

  const handleRoomChange = (
    type: 'bathroom' | 'bedroom' | 'kitchen',
    index: number,
    area: number
  ) => {
    const rooms = [...(meta[type] || [])];
    rooms[index] = { area };

    const updatedMeta = {
      ...meta,
      [type]: rooms,
      [`${type}Count`]: rooms.length,
    };

    setMeta(updatedMeta);
    onChange?.(updatedMeta);
  };

  const addRoom = (type: 'bathroom' | 'bedroom' | 'kitchen') => {
    const rooms = [...(meta[type] || []), { area: 0 }];

    const updatedMeta = {
      ...meta,
      [type]: rooms,
      [`${type}Count`]: rooms.length,
    };

    setMeta(updatedMeta);
    onChange?.(updatedMeta);
  };

  const removeRoom = (type: 'bathroom' | 'bedroom' | 'kitchen', index: number) => {
    const rooms = [...(meta[type] || [])];
    rooms.splice(index, 1);

    const updatedMeta = {
      ...meta,
      [type]: rooms,
      [`${type}Count`]: rooms.length,
    };

    setMeta(updatedMeta);
    onChange?.(updatedMeta);
  };

  const renderRooms = (type: 'bathroom' | 'bedroom' | 'kitchen', label: string) => {
    const rooms = meta[type] || [];
    const count = rooms.length;

    return (
      <Row gutter={16}>
        <Col span={24} lg={20}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16 }}>
              {label}: {count}
            </Text>
          </div>
        </Col>
        <Col
          span={24}
          lg={4}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
          <Button type="dashed" onClick={() => addRoom(type)} icon={<PlusOutlined />}>
            Add {label}
          </Button>
        </Col>
        <Col span={24}>
          {rooms.map((room, index) => (
            <div key={index} style={{ marginBottom: 10 }}>
              <Row
                align="middle"
                gutter={8}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '6px',
                  background: '#ffffff',
                }}>
                <Col flex="auto">
                  <Row align="middle" gutter={12}>
                    <Col flex="none" style={{ width: 80 }}>
                      <Text strong>Room {index + 1}:</Text>
                    </Col>
                    <Col flex="none" style={{ width: 45 }}>
                      <Text>Area</Text>
                    </Col>
                    <Col flex="auto">
                      <InputNumber
                        value={room.area}
                        onChange={value => handleRoomChange(type, index, Number(value))}
                        style={{ width: '100%' }}
                        controls={false}
                        variant="outlined"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col flex="none">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeRoom(type, index)}
                    style={{
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                </Col>
              </Row>
            </div>
          ))}
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: 0 }}>
      {renderRooms('bathroom', 'Bathroom')}
      <Divider style={{ margin: '16px 0' }} />

      {renderRooms('bedroom', 'Bedroom')}
      <Divider style={{ margin: '16px 0' }} />

      {renderRooms('kitchen', 'Kitchen')}
      <Divider style={{ margin: '24px 0' }} />

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label={<Text strong>Total Area</Text>} style={{ marginBottom: 16 }}>
            <InputNumber
              value={meta.totalArea}
              onChange={value => updateMeta({ totalArea: Number(value) })}
              style={{ width: '100%' }}
              controls={false}
              bordered={true}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<Text strong>Outer Area</Text>} style={{ marginBottom: 16 }}>
            <InputNumber
              value={meta.outerArea}
              onChange={value => updateMeta({ outerArea: Number(value) })}
              style={{ width: '100%' }}
              controls={false}
              bordered={true}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label={<Text strong>Type</Text>} style={{ marginBottom: 16 }}>
            <Select
              value={meta.type}
              onChange={value => updateMeta({ type: value })}
              style={{ width: '100%' }}
              options={[
                { value: 'house', label: 'House' },
                { value: 'shed', label: 'Shed' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={<Text strong>Width</Text>} style={{ marginBottom: 16 }}>
            <InputNumber
              value={meta.width}
              onChange={value => updateMeta({ width: Number(value) })}
              style={{ width: '100%' }}
              controls={false}
              bordered={true}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={<Text strong>Length</Text>} style={{ marginBottom: 16 }}>
            <InputNumber
              value={meta.length}
              onChange={value => updateMeta({ length: Number(value) })}
              style={{ width: '100%' }}
              controls={false}
              bordered={true}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default MetaEditor;
